import { AUTH_PERSIST_KEY } from '../../Constants/Auth/Auth.constants'
import { I18N_PERSIST_KEY } from '../../Constants/I18n/I18n.constants'
import { STORAGE_PERSIST_KEY } from '../../Constants/Storage/Storage.constants'
import AnalyticsMemoryAdapter from '../../Services/Analytics/Adapter/Memory/AnalyticsMemory.adapter'
import MockAnalyticsService from '../../Services/Analytics/Analytics.service.mock'
import AuthMemoryAdapter from '../../Services/Auth/Adapter/Memory/AuthMemory.adapter'
import MockAuthService from '../../Services/Auth/Auth.service.mock'
import DeliveryService from '../../Services/Delivery/Delivery.service'
import EntityService from '../../Services/Entity/Entity.service'
import MockHttpService from '../../Services/Http/Http.service.mock'
import I18nService from '../../Services/I18n/I18n.service'
import MockOrderService from '../../Services/Order/Order.service.mock'
import StorageMemoryAdapter from '../../Services/Storage/Adapter/Memory/StorageMemory.adapter'
import StorageService from '../../Services/Storage/Storage.service'
import DocketService from '../../Services/Docket/Docket.service'
import MockUserService from '../../Services/User/User.service.mock'
import i18nStore from '../../Support/I18n/Store/I18nStore.support'
import OrderService from 'Services/Order/Order.service'
import { Services } from 'types/Services'
import NotificationService from 'Services/Notification/Notification.service'
import HereMapsService from 'Services/HereMaps/HereMaps.service'
import envGet from 'Support/Env/Get/EnvGet.support'

const hereMapsApiKey = envGet({ key: `REACT_APP_BORAL_HERE_MAPS_API_KEY` })

/**
 * Create an implementation of `Services` that use our mocked versions instead
 * @export
 * @returns {Services} The mocked out services
 */
export const createMockServices = (): Services => {
  const httpService = new MockHttpService()
  const i18nService = new I18nService({
    storageService: new StorageService({
      key: I18N_PERSIST_KEY,
      storageAdapter: new StorageMemoryAdapter({ key: I18N_PERSIST_KEY, store: { [I18N_PERSIST_KEY]: i18nStore.get() } }),
    }),
  })
  const storageService = new StorageService({
    key: STORAGE_PERSIST_KEY,
    storageAdapter: new StorageMemoryAdapter({ key: STORAGE_PERSIST_KEY }),
  })
  const analyticsService = new MockAnalyticsService({
    analyticsAdapter: new AnalyticsMemoryAdapter({
      config: {},
    }),
  })
  const authService = new MockAuthService({
    authAdapter: new AuthMemoryAdapter({
      storageService: new StorageService({
        key: AUTH_PERSIST_KEY,
        storageAdapter: new StorageMemoryAdapter({ key: AUTH_PERSIST_KEY }),
      }),
    }),
  })
  const deliveryService = new DeliveryService({ authService, httpService })
  const entityService = new EntityService()
  const orderService: any = new MockOrderService({ authService, httpService })
  const docketService = new DocketService({ authService, httpService })
  const userService = new MockUserService({ httpService })
  const notificationService = new NotificationService({ authService, httpService })
  const platform = window.H && window.H.service ? new window.H.service.Platform({
    apikey: hereMapsApiKey,
  }) : {};

  const hereMapsService = new HereMapsService({ platform, authService, httpService });

  const services: Services = {
    analyticsService,
    authService,
    deliveryService,
    entityService,
    httpService,
    i18nService,
    orderService,
    storageService,
    userService: userService as any,
    docketService,
    notificationService,
    hereMapsService: hereMapsService as any,
  }
  return services
}

/**
 * Create the mock services in such a way that no mock service is ever initialised
 * @export
 * @type {Services}
 */
const mockServices = {
  __services: null as unknown,
  /**
   * If `this._services` is ever called, then we know that we are in test mode and then we can initialise the mocked services
   */
  get _services(): Services {
    if (!this.__services) {
      this.__services = createMockServices()
    }
    return this.__services as Services
  },

  /**
   * The `AnalyticsService`
   * @type {AnalyticsService}
   */
  get analyticsService() {
    return this._services.analyticsService
  },

  /**
   * The `AuthService`
   * @type {AuthService}
   */
  get authService() {
    return this._services.authService
  },

  /**
   * The `DeliveryService`
   * @type {DeliveryService}
   */
  get deliveryService() {
    return this._services.deliveryService
  },

  /**
   * The `EntityService`
   * @type {EntityService}
   */
  get entityService() {
    return this._services.entityService
  },

  /**
   * The `HttpService`
   * @type {HttpService}
   */
  get httpService() {
    return this._services.httpService
  },

  /**
   * The `I18nService`
   * @type {I18nService}
   */
  get i18nService() {
    return this._services.i18nService
  },

  get orderService(): OrderService {
    return this._services.orderService
  },

  /**
   * The `StorageService`
   * @type {StorageService}
   */
  get storageService() {
    return this._services.storageService
  },

  /**
   * The `UserService`
   * @type {UserService}
   */
  get userService() {
    return this._services.userService
  },

  get docketService() {
    return this._services.docketService
  },
  get notificationService() {
    return this._services.notificationService
  },
  get hereMapsService() {
    return this._services.hereMapsService
  },
}

export default mockServices
